// Which events show (the tab, Free, price, types, search, hidden ones), as one memoized set the map, the list and
// the pointers all read (docs/ARCHITECTURE.md §2.3; port of web/filters.js). Recomputed only when the events, the
// filter, the minute or the profile change; otherwise every caller gets the same arrays back and the same
// `version`, so the map can tell nothing changed. A plain function, not React state.
import type { FeedEvent } from "../data/event";
import { timed } from "../perf";
import { RULES, type Box } from "../shared/rules";
import { clockStore } from "./clock";
import { feedStore } from "./feed";
import { isHiddenEvent, profileStore, type ProfileView } from "./profile";
import { isSearching, viewStore, type Filter } from "./view";

const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
// Each event's searchable text, folded once (events are replaced, not edited, when they change).
const haystacks = new WeakMap<FeedEvent, string>();
function haystack(e: FeedEvent) {
  let hay = haystacks.get(e);
  if (hay == null) {
    hay = fold([e.title, e.description || "", e.venue_name || "", e.address || "", ...e.tags].join(" "));
    haystacks.set(e, hay);
  }
  return hay;
}
const searchWords = (q: string) => fold(q).split(/\s+/).filter(Boolean);
export const lowestPrice = (e: FeedEvent) => (e.is_free === true ? 0 : e.price_min ?? e.price_max ?? null);

// All filters except time. Unknown-price events always pass the cost filters (shown as "price unknown").
function nonTimeFilter(f: Filter, view: ProfileView) {
  const words = searchWords(f.search), interests = view.interests as Set<string>, tags = f.tags as Set<string>;
  return (e: FeedEvent) => {
    if (f.freeOnly && !RULES.passesFreeFilter(e)) return false;
    if (f.maxPrice != null) { const p = lowestPrice(e); if (p != null && p > f.maxPrice) return false; }
    if (!RULES.matchesTypes(e, tags, f.allInterests, interests)) return false;
    if (words.length) { const hay = haystack(e); if (!words.every((w) => hay.includes(w))) return false; }
    return true;
  };
}

export const byStart = (a: FeedEvent, b: FeedEvent) => +a.start - +b.start;

export interface Filtered {
  /** This tab's matches, soonest first. */
  primary: FeedEvent[];
  primaryIds: Set<string>;
  /** While searching: matches beyond the tab (as far as any tab reaches), the faded pins. */
  secondary: FeedEvent[];
  /** Matches after the tab, for "soonest" (sorted on first use). */
  later: () => FeedEvent[];
  /** Changes whenever primary or secondary do. */
  version: number;
}

let memo: { events: unknown; filter: unknown; view: unknown; minute: number; value: Filtered | null } =
  { events: null, filter: null, view: null, minute: -1, value: null };
let version = 0;

export function filtered(): Filtered {
  return timed("filtered", compute);
}
function compute(): Filtered {
  const events = feedStore.getState().events, f = viewStore.getState().filter, view = profileStore.getState().view;
  const now = clockStore.getState().now, minute = Math.floor(+now / 60e3);
  if (memo.value && memo.events === events && memo.filter === f && memo.view === view && memo.minute === minute) return memo.value;
  const w = RULES.tabWindow(f.time, now), passes = nonTimeFilter(f, view), searching = isSearching(f);
  const reach = searching ? RULES.tabsReach(now) : null;
  const primary: FeedEvent[] = [], secondary: FeedEvent[] = [], rest: FeedEvent[] = [];
  for (const e of events) {
    if (RULES.hasEnded(e, now) || !passes(e) || isHiddenEvent(e, view)) continue;
    // In the tab (RULES.inTab, with the tab's window worked out once).
    if (e.start < w.end && RULES.effectiveEnd(e) > w.start) primary.push(e);
    else if (e.start > now) { rest.push(e); if (reach && e.start < reach) secondary.push(e); }
  }
  primary.sort(byStart); secondary.sort(byStart);
  let later: FeedEvent[] | null = null;
  const laterFn = () => (later ??= rest.sort(byStart));
  // The same events as before (a new minute, a refresh that changed nothing shown): same arrays, same version.
  const old = memo.value, same = (a: FeedEvent[], b: FeedEvent[]) => a.length === b.length && a.every((e, i) => e === b[i]);
  const value: Filtered = old && same(old.primary, primary) && same(old.secondary, secondary)
    ? { ...old, later: laterFn }
    : { primary, primaryIds: new Set(primary.map((e) => e.id)), secondary, version: ++version, later: laterFn };
  memo = { events, filter: f, view, minute, value };
  return value;
}

/** Whether a point is in a box ([w, s, e, n]; w > e crosses the antimeridian). */
export function inBounds(b: Box, e: { lat: number; lng: number }) {
  if (e.lat < b[1] || e.lat > b[3]) return false;
  return b[0] <= b[2] ? e.lng >= b[0] && e.lng <= b[2] : e.lng >= b[0] || e.lng <= b[2];
}
export function inViewTest() {
  const cam = viewStore.getState().camera;
  return cam ? (e: FeedEvent) => inBounds(cam.bounds, e) : () => true;
}

/** When the visible area has nothing in the tab: the soonest upcoming match in view, else the soonest anywhere. */
export function soonestFallback(): FeedEvent | null {
  const { primary, later } = filtered(), inView = inViewTest();
  if (feedStore.getState().source === "loading" || primary.some(inView)) return null;
  const all = later();
  return all.find(inView) || all[0] || null;
}
